import React, { useMemo, useRef } from 'react';
import { useGesture } from 'react-use-gesture';
import { useSpring, a, config } from 'react-spring/three';
import { Mesh, MeshBasicMaterial, Texture } from 'three';
import { ReactThreeFiber, useThree } from 'react-three-fiber';
import { merge } from 'lodash';

type TButtonProps = {
  onClick: (e?: any) => void;
  position?: ReactThreeFiber.Vector3;
  rotation?: ReactThreeFiber.Euler;
  renderOrder?: number;

  defaultState: {
    main?: {
      material?: {
        map?: Texture;
        color?: string;
        transparent?: boolean;
        opacity?: number;
      };
      size?: number;
      width?: number;
      height?: number;
      scale?: ReactThreeFiber.Vector3;
      geometry?: 'circle' | 'plane';
    };

    icon?: {
      material?: {
        map?: Texture;
        color?: string;
        transparent?: boolean;
        opacity?: number;
      };
      size?: number;
      width?: number;
      height?: number;
      scale?: ReactThreeFiber.Vector3;
      geometry?: 'circle' | 'plane';
    };
  };

  hoverState?: {
    main?: {
      map?: Texture;
      color?: string;
      opacity?: number;

      scale?: ReactThreeFiber.Vector3;
    };

    icon?: {
      map?: Texture;
      color?: string;
      opacity?: number;

      scale?: ReactThreeFiber.Vector3;
    };

    cursorClass?: string;
  };

  children?: React.ReactChild;
};

function filterExistingProps<T extends object>(obj: T) {
  return Object.fromEntries(Object.entries(obj).filter(([_k, v]) => v !== undefined));
}

const Button = ({ children, defaultState, hoverState, position, rotation, renderOrder, onClick }: TButtonProps) => {
  const defaultProps = useMemo(
    () =>
      merge(
        {
          main: {
            size: 0.1,
            scale: [1, 1, 1],
            material: {
              color: '#fff',
              opacity: 1,
            },
            geometry: 'plane',
          },
        },
        defaultState
      ),
    [defaultState]
  );

  const hoverProps = useMemo(
    () =>
      merge(
        {
          main: {
            scale: [0.9, 0.9, 0.9],
          },
          icon: {},
          cursorClass: 'cursor-pointer',
        },
        hoverState
      ),
    [hoverState]
  );

  const mainSpringInitial = useMemo(
    () =>
      filterExistingProps({
        color: defaultProps.main.material.color,
        opacity: defaultProps.main.material.opacity,
        scale: defaultProps.main.scale,
      }),
    [defaultProps.main.material.color, defaultProps.main.material.opacity, defaultProps.main.scale]
  );

  // const iconSpringInitial = useMemo(
  //   () =>
  //     filterExistingProps({
  //       color: defaultProps?.icon?.material?.color || '#fff',
  //       opacity: defaultProps?.icon?.material?.opacity || 1,
  //       scale: defaultProps?.icon?.scale || [1, 1, 1],
  //     }),
  //   [defaultProps]
  // );

  const [mainSpring, setMainSpring] = useSpring(() => ({
    ...mainSpringInitial,
    config: config.gentle,
  }));

  // const [iconSpring, setIconSpring] = useSpring(() => ({
  //   ...iconSpringInitial,
  //   config: config.gentle,
  // }));

  const { gl } = useThree();

  const mainRef = useRef<Mesh>();

  const bind = useGesture({
    onClick: ({ event }) => {
      const evt = event as any;
      evt.nativeEvent.preventDefault();
      evt.nativeEvent.stopPropagation();
      event.stopPropagation();
      onClick(event);
    },
    onHover: ({ hovering, event }) => {
      const evt = event as any;
      evt.nativeEvent.preventDefault();
      evt.nativeEvent.stopPropagation();
      event.stopPropagation();

      if (hovering) {
        setTimeout(() => {
          gl.domElement.classList.add(hoverProps.cursorClass);
        });
      } else {
        gl.domElement.classList.remove(hoverProps.cursorClass);
      }

      const { map: mainMapHover, ...mainSpringHover } = hoverProps.main;

      if (mainRef.current) {
        if (hovering && mainMapHover) {
          (mainRef.current.material as MeshBasicMaterial).map = mainMapHover;
        } else {
          (mainRef.current.material as MeshBasicMaterial).map = defaultProps.main.material.map || null;
        }
      }

      setMainSpring({
        ...(hovering ? mainSpringHover : mainSpringInitial),
      });
    },
  });

  return (
    <a.mesh position={position} rotation={rotation} renderOrder={renderOrder} {...mainSpring} {...bind()} ref={mainRef}>
      {defaultProps.main.geometry === 'circle' ? (
        <circleBufferGeometry args={[defaultProps.main.size, 32]} />
      ) : (
        <planeBufferGeometry
          args={[defaultProps.main.width || defaultProps.main.size, defaultProps.main.height || defaultProps.main.size]}
        />
      )}
      <a.meshBasicMaterial {...defaultProps.main.material} {...mainSpring} />

      {defaultProps.icon ? (
        <a.mesh position={[0, 0, 0.0001]} renderOrder={renderOrder} /*  {...iconSpring} */>
          <planeBufferGeometry
            args={[
              defaultProps.icon.width || defaultProps.icon.size,
              defaultProps.icon.height || defaultProps.icon.size,
            ]}
          />
          <a.meshBasicMaterial {...defaultProps.icon.material} /*  {...iconSpring} */ />
        </a.mesh>
      ) : null}

      {children}
    </a.mesh>
  );
};

export default Button;
